import { Globe, Check } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useNavigate, useRouterState } from "@tanstack/react-router";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useLanguage } from "@/i18n/useLanguage";
import type { AppLang } from "@/i18n";
import { detectLocale, toEnPath, toSvPath } from "@/i18n/routeMap";

interface Props {
  /**
   * "pill"  — för bruk i ljust SiteHeader (mörkt content på vit pill).
   * "ghost" — för MobileMenu (ljust content på mörk bakgrund).
   */
  variant?: "pill" | "ghost";
}

const OPTIONS: Array<{ code: AppLang; labelKey: "lang.swedish" | "lang.english"; flag: string }> = [
  { code: "sv", labelKey: "lang.swedish", flag: "🇸🇪" },
  { code: "en", labelKey: "lang.english", flag: "🇬🇧" },
];

export function LanguageSwitcher({ variant = "pill" }: Props) {
  const { t } = useTranslation();
  const { lang, setLang } = useLanguage();
  const navigate = useNavigate();
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  const handleSelect = (next: AppLang) => {
    setLang(next);
    const currentLocale = detectLocale(pathname);
    if (currentLocale === next) return;
    const target = next === "en" ? toEnPath(pathname) : toSvPath(pathname);
    if (target && target !== pathname) {
      void navigate({ to: target as never });
    }
  };

  const triggerClass =
    variant === "pill"
      ? "relative inline-flex h-9 w-9 items-center justify-center rounded-full text-black/65 transition-colors hover:bg-black/[0.06] hover:text-black focus:outline-none focus-visible:ring-2 focus-visible:ring-black/30"
      : "inline-flex h-10 w-10 items-center justify-center rounded-full bg-white/5 text-foreground transition-colors hover:bg-white/10";

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        className={triggerClass}
        aria-label={t("lang.switchTo")}
        title={t("lang.label")}
      >
        <Globe className="h-4 w-4" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-44">
        <DropdownMenuLabel>{t("lang.label")}</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {OPTIONS.map((opt) => {
          const active = opt.code === lang;
          return (
            <DropdownMenuItem
              key={opt.code}
              onSelect={() => handleSelect(opt.code)}
              className="flex items-center justify-between gap-2"
            >
              <span className="flex items-center gap-2">
                <span aria-hidden className="text-base leading-none">
                  {opt.flag}
                </span>
                <span>{t(opt.labelKey)}</span>
              </span>
              {active && <Check className="h-3.5 w-3.5 text-mint" />}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
